import { useEffect, useMemo, useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import {
  rackHeightM,
  rescaleParts,
  splitEvenly,
  splitIntoSlots,
  type RackKind,
} from '@eombor/contracts';
import { Button, Input, cn } from '@eombor/ui';
import { Select } from '../../../shared/ui/Select.js';
import { t } from '../../../shared/i18n/index.js';
import type { DraftRack } from '../model/builder.store.js';

/**
 * Stellaj xossalari — TASHQARIDAN ICHKARIGA.
 *
 * Ombordagi odam stellajni shunday o'lchaydi: avval ruletka bilan butun
 * javonning bo'yini, chuqurligini va balandligini oladi, keyin nechta
 * qatorligini sanaydi va shundan so'ng har bir proyomga alohida o'tadi.
 * Forma shu tartibni takrorlaydi.
 *
 * Umumiy o'lcham bilan qismlar IKKI TOMONLAMA bog'langan: umumiy uzunlik
 * o'zgarsa proyomlar nisbatini saqlagan holda qayta taqsimlanadi, bitta
 * proyom o'zgarsa umumiy uzunlik o'zi yangilanadi. Ikkalasini alohida
 * kiritish ularni bir-biriga zid qilib qo'yish imkonini berardi.
 *
 * O'lchov birligi — SANTIMETR. Stellajning hamma standart o'lchami 10 mm
 * ning karrasi (balka 2700, rama 1100, yarus 1800), ya'ni santimetrda hech
 * narsa yo'qolmaydi: 270, 110, 180. Lekin raqam ikki belgiga qisqaradi va
 * kasr umuman kerak bo'lmaydi — metrda "2.7" o'rniga "27" terib yuborish
 * juda oson. Metrga o'girish faqat saqlash oldidan bo'ladi.
 */

/** Ekran birligi metrda: 1 sm. */
const UNIT_M = 0.01;

export interface RackFormValues {
  code: string;
  name: string;
  /** `FLOOR` — ochiq maydon: bir qavat, temirsiz. */
  kind: RackKind;
  depthM: number;
  bays: number[];
  levelHeights: number[];
  rotationDeg: number;
}

/**
 * Standart javon: 200 × 110 × 200 sm — bitta proyom, 4 × 50 sm qator.
 *
 * O'lcham QARSHI OMBORIDAGI javondan olingan: eni ham, bo'yi ham 2 metr,
 * ichida to'rtta qator. Ilgari standart balandlik 720 sm edi (4 × 180) va
 * u ombordagi hech narsaga to'g'ri kelmasdi — har safar qo'lda tuzatishga
 * to'g'ri kelardi.
 *
 * PROYOM — asosiy birlik va u avtomatik bo'linmaydi. Kiritilgan uzunlik
 * aynan o'sha bitta uzun proyom bo'lib chiqadi. Bo'lish — ATAYLAB
 * qilinadigan ish: operator pastdagi ro'yxatda proyomni kerakli
 * qismlarga bo'ladi va mahsulot o'sha qismlarga joylashadi.
 *
 * YARUS boshqacha ishlaydi: u avtomatik bo'linadi (`splitIntoSlots`),
 * chunki javonning balandligi bo'ylab qatorlar bir xil qadamda ketadi va
 * ularni bittalab terish ortiqcha ish bo'lardi.
 */
const DEFAULT_LEVEL_CM = 50;

const DEFAULTS: RackFormValues = {
  code: '01',
  name: '',
  kind: 'RACK',
  depthM: 1.1,
  bays: [2],
  levelHeights: [0.5, 0.5, 0.5, 0.5],
  rotationDeg: 0,
};

/**
 * OCHIQ MAYDON standarti: 600 × 300 sm, uchta o'rin, 200 sm taxlash.
 *
 * Javonning standartidan boshqacha va shunday bo'lishi ham kerak: maydonga
 * qo'yiladigan mol javonga sig'magani uchun yerda turibdi — rels, shpal,
 * g'ildirak juftligi. Chuqurligi ham javonnikidan katta: yonidan emas,
 * yuk ko'targich bilan yuqoridan olinadi.
 *
 * `levelHeights` — bitta son va u qavat EMAS: maydonda qavat yo'q. Bu
 * TAXLASH CHEGARASI: «bu maydonga 2 metrdan baland taxlanmaydi».
 */
const FLOOR_DEFAULTS: Pick<RackFormValues, 'depthM' | 'bays' | 'levelHeights'> = {
  depthM: 3,
  bays: [2, 2, 2],
  levelHeights: [2],
};

const valuesOf = (rack: DraftRack | null, defaultCode?: string): RackFormValues =>
  rack === null
    ? {
        ...DEFAULTS,
        ...(defaultCode === undefined ? {} : { code: defaultCode }),
        bays: [...DEFAULTS.bays],
        levelHeights: [...DEFAULTS.levelHeights],
      }
    : {
        code: rack.code,
        name: rack.name ?? '',
        kind: rack.kind,
        depthM: rack.depthM,
        // Ustada bo'linish BITTA: 1-qatorniki namuna qilib olinadi.
        bays: [...(rack.bays[0] ?? [])],
        levelHeights: [...rack.levelHeights],
        rotationDeg: rack.pos.rotationDeg,
      };

const cm = (metres: number): string => String(Math.round(metres / UNIT_M));

/** Bo'sh maydon `NaN` ga aylanmasligi kerak — nol qaytariladi va xato chiqadi. */
const toMetres = (raw: string): number => {
  const value = Number(raw.replace(',', '.'));
  return Number.isFinite(value) ? Math.round(value) * UNIT_M : 0;
};

interface FieldErrors {
  code?: string;
  bays?: string;
  levelHeights?: string;
  depthM?: string;
}

const validate = (values: RackFormValues): FieldErrors => {
  const errors: FieldErrors = {};
  if (!/^\d{2,3}$/.test(values.code)) errors.code = t('builder.errorRackCode');
  if (values.bays.length === 0 || values.bays.some((bay) => bay <= 0)) {
    errors.bays = t('builder.errorBay');
  }
  if (values.levelHeights.length === 0 || values.levelHeights.some((h) => h <= 0)) {
    errors.levelHeights = t('builder.errorLevelHeight');
  }
  if (values.depthM <= 0) errors.depthM = t('builder.errorDepth');
  return errors;
};

const sumOf = (values: readonly number[]): number => values.reduce((total, v) => total + v, 0);

/** Ro'yxatning bitta elementini almashtiradi — o'zgarmas nusxa qaytadi. */
const replaceAt = (list: number[], index: number, value: number): number[] =>
  list.map((item, i) => (i === index ? value : item));

interface TotalInputProps {
  label: string;
  hint: string;
  valueCm: number;
  onCommit: (centimetres: number) => void;
}

/**
 * Umumiy o'lcham maydoni — qiymat FOKUS TUGAGANDA qabul qilinadi.
 *
 * Har bosilgan tugmada qayta taqsimlansa, "10000" ni terish ketma-ket
 * 1 → 10 → 100 → 1000 → 10000 mm degan beshta qayta taqsimlashga aylanardi
 * va har birida yaxlitlash nisbatni bir oz buzardi: 3:1:1 dan chiqqan
 * natija umuman boshqa son bo'lib qolardi. Shuning uchun terish paytida
 * maydon oddiy matn bo'lib turadi va faqat chiqishda hisoblanadi.
 */
const TotalInput = ({ label, hint, valueCm, onCommit }: TotalInputProps) => {
  const [draft, setDraft] = useState<string | null>(null);

  const commit = (): void => {
    if (draft === null) return;
    const next = Number(draft.replace(',', '.'));
    if (Number.isFinite(next) && next > 0) onCommit(next);
    setDraft(null);
  };

  return (
    <Input
      mono
      type="number"
      min={1}
      step={10}
      label={label}
      hint={hint}
      suffix={t('builder.unitCm')}
      value={draft ?? String(valueCm)}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        event.currentTarget.blur();
      }}
    />
  );
};

interface PartListProps {
  /** Guruh sarlavhasi — "Proyomlar". */
  label: string;
  /** Bitta maydon nomi — "Proyom". Ko'plikdan FARQ QILADI, chunki proyom va
   *  qator maydonlari bitta formada yonma-yon turadi va nomi bilan ajraladi. */
  itemLabel: string;
  unit: string;
  values: number[];
  error: string | undefined;
  onChange: (next: number[]) => void;
  /**
   * `divide` — "+" umumiy o'lchamni SAQLAB qismlarga bo'ladi (proyomlar).
   * `append` — "+" oxirgi qismning nusxasini qo'shadi va o'lcham o'sadi.
   *
   * Proyom uchun `divide` yagona to'g'ri xatti-harakat: omborchi javonni
   * uzaytirmoqchi emas, 200 sm li proyomni ikkita 100 sm li o'ringa
   * BO'LMOQCHI va har biriga o'z etiketkasini yopishtirmoqchi. Ilgari
   * "+" javonni 200 dan 400 ga cho'zib yuborardi.
   */
  mode: 'divide' | 'append';
  hint?: string;
}

/**
 * Qismlar ro'yxati — proyomlar yoki qatorlar.
 *
 * Qo'shishda OXIRGI qismning o'lchami takrorlanadi: stellaj bir xil
 * balkadan yig'iladi, ya'ni keyingi proyom deyarli har doim oldingisi
 * bilan bir xil bo'ladi va odam raqamni qayta terib o'tirmasligi kerak.
 */
const PartList = ({
  label,
  itemLabel,
  unit,
  values,
  error,
  onChange,
  mode,
  hint,
}: PartListProps) => (
  <fieldset className="flex flex-col gap-2">
    <div className="flex items-center justify-between gap-2">
      <legend className="text-text-muted text-xs font-medium tracking-wide uppercase">
        {label} · {values.length}
      </legend>
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={t('builder.removePart', { label })}
          disabled={values.length <= 1}
          onClick={() =>
            onChange(
              mode === 'divide'
                ? splitEvenly(sumOf(values), values.length - 1, UNIT_M)
                : values.slice(0, -1),
            )
          }
        >
          <Minus size={14} aria-hidden />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={t('builder.addPart', { label })}
          onClick={() =>
            onChange(
              mode === 'divide'
                ? splitEvenly(sumOf(values), values.length + 1, UNIT_M)
                : [...values, values.at(-1) ?? 1],
            )
          }
        >
          <Plus size={14} aria-hidden />
        </Button>
      </div>
    </div>

    <div className="grid grid-cols-2 gap-2">
      {values.map((value, index) => (
        <Input
          // Indeks kalit sifatida ATAYLAB: qismlarning o'zida identifikator
          // yo'q va tartib raqami ularning yagona belgisi.
          key={index}
          mono
          type="number"
          min={1}
          step={10}
          label={`${itemLabel} ${index + 1}`}
          suffix={unit}
          value={cm(value)}
          onChange={(event) => onChange(replaceAt(values, index, toMetres(event.target.value)))}
        />
      ))}
    </div>

    {error === undefined ? null : (
      <p role="alert" className="text-danger text-xs">
        {error}
      </p>
    )}

    {hint === undefined ? null : <p className="text-text-muted text-xs">{hint}</p>}
  </fieldset>
);

export interface RackEditorProps {
  /** `null` — yangi stellaj; aks holda mavjudini tahrirlash. */
  rack: DraftRack | null;
  onSave: (values: RackFormValues) => void;
  /** Har o'zgarishda chaqiriladi — ustadagi 3D shu bilan tirik turadi. */
  onChange?: (values: RackFormValues) => void;
  onCancel?: () => void;
  /** Saqlash tugmasining matni: ustada "Qo'shish", panelda "Saqlash". */
  submitLabel?: string;
  /** Yangi javonga taklif qilinadigan kod — zonadagi navbatdagisi. */
  defaultCode?: string;
  className?: string;
}

export const RackEditor = ({
  rack,
  onSave,
  onChange,
  onCancel,
  submitLabel,
  defaultCode,
  className,
}: RackEditorProps) => {
  const [values, setValues] = useState<RackFormValues>(() => valuesOf(rack, defaultCode));
  const [touched, setTouched] = useState(false);

  /*
   * YARUS BALANDLIGI — javonning o'zida saqlanmaydi.
   *
   * Bazada faqat yaruslar ro'yxati bor va bu to'g'ri: real javonning
   * pastki yarusi ko'pincha balandroq bo'ladi va o'sha holat saqlanishi
   * kerak. Bu qiymat esa faqat "balandlikni nechaga bo'lish" savoliga
   * javob beradi, ya'ni muharrirning ish quroli.
   *
   * Mavjud javonni tahrirlashda o'rtacha qiymat olinadi — operator ochgan
   * oynada o'zi kiritgan songa yaqin raqamni ko'radi, standartni emas.
   */
  const [levelCm, setLevelCm] = useState(() =>
    rack === null
      ? DEFAULT_LEVEL_CM
      : Math.max(1, Math.round(rackHeightM(rack) / rack.levelHeights.length / UNIT_M)),
  );

  /* Tashqi kuzatuvchi (3D) qiymat o'zgarganda xabardor bo'ladi. `useEffect`
     da, `setValues` ichida emas: holatni yangilash paytida boshqa
     komponentni yangilash React da taqiqlangan. */
  useEffect(() => {
    onChange?.(values);
  }, [values, onChange]);

  const errors = useMemo(() => validate(values), [values]);
  const invalid = Object.keys(errors).length > 0;

  const isFloor = values.kind === 'FLOOR';

  /**
   * TUR ALMASHDI — o'lchamlar ham almashadi.
   *
   * Eski o'lchamni qoldirish yaramaydi: 110 sm chuqurlikdagi «maydon» ham,
   * 50 sm balandlikdagi «taxlash chegarasi» ham ombordagi hech narsaga
   * to'g'ri kelmaydi. Odam turni tanlaganda o'sha turning standartini
   * ko'rishi kerak, keyin uni o'zi tuzatadi.
   */
  const setKind = (next: RackKind): void => {
    setValues((current) => ({
      ...current,
      kind: next,
      ...(next === 'FLOOR'
        ? { ...FLOOR_DEFAULTS, bays: [...FLOOR_DEFAULTS.bays], levelHeights: [...FLOOR_DEFAULTS.levelHeights] }
        : { depthM: DEFAULTS.depthM, bays: [...DEFAULTS.bays], levelHeights: [...DEFAULTS.levelHeights] }),
    }));
  };

  /* Ustada hamma qator bir xil bo'linadi, ya'ni katak soni ko'paytma. */
  const total = useMemo(
    () => values.bays.length * values.levelHeights.length,
    [values.bays.length, values.levelHeights.length],
  );

  const removed = rack === null ? 0 : Math.max(0, rack.serverCells - total);
  const added = rack === null ? total : Math.max(0, total - rack.serverCells);

  const set = <K extends keyof RackFormValues>(key: K, value: RackFormValues[K]): void => {
    setValues((current) => ({ ...current, [key]: value }));
  };

  /**
   * UZUNLIK o'zgardi — proyomlar SONI saqlanadi, o'lchami qayta taqsimlanadi.
   *
   * Standart javonda bitta proyom bor, ya'ni kiritilgan uzunlik aynan
   * o'sha bitta uzun proyom bo'lib chiqadi — avtomatik bo'linish YO'Q.
   * Operator proyomni pastdagi ro'yxatda o'zi bo'lgan bo'lsa, o'sha
   * bo'linish saqlanadi: uni jimgina bitta proyomga qaytarish qilingan
   * ishni yo'q qilardi.
   */
  const setLength = (totalM: number): void => {
    if (totalM <= 0) return;
    // Qadam SANTIMETR: maydonlardagi butun sonlar aniq yig'ilsin.
    setValues((current) => ({ ...current, bays: rescaleParts(current.bays, totalM, UNIT_M) }));
  };

  /**
   * BALANDLIK yoki bitta yarus balandligi o'zgardi — yaruslar qayta
   * bo'linadi va SONI balandlikdan hisoblanadi: 720 / 180 = 4 yarus.
   *
   * `levelCm` — OPERATORNING SO'ROVI va u natijaga qarab o'zgarmaydi.
   * Bir vaqt natija maydonga qaytarilardi va bu terilgan sonni yeb
   * qo'yardi: "yarus 50" (1.8 m javonda 4 x 45 chiqadi) natijadan
   * 45 qaytarardi, keyin "balandlik 200" o'sha 45 ga bo'linib 5 x 40
   * berardi — odam 50 so'ragan bo'lsa ham. Endi so'ralgan chegara
   * joyida qoladi va `splitIntoSlots` uni hech qachon oshirmaydi.
   */
  const resplitLevels = (totalM: number, levelMetres: number): void => {
    if (totalM <= 0 || levelMetres <= 0) return;
    set('levelHeights', splitIntoSlots(totalM, levelMetres, UNIT_M));
  };

  const show = (message: string | undefined): string | undefined =>
    touched ? message : undefined;

  return (
    <form
      // Brauzerning o'z validatsiyasi o'chirilgan: uning xabari tizim tilida
      // chiqadi va bizning o'zbekcha matnimizni bosib ketadi. Tekshiruv
      // `validate()` da — bitta joyda va bitta tilda.
      noValidate
      className={cn('flex flex-col gap-4', className)}
      onSubmit={(event) => {
        event.preventDefault();
        setTouched(true);
        if (!invalid) onSave(values);
      }}
    >
      {/* TUR faqat YARATISHDA so'raladi: javonni maydonga aylantirish
          kataklarning ma'nosini o'zgartirardi, etiketkalar esa javonda
          yopishtirilgan holicha qolardi. */}
      {rack === null ? (
        <Select
          label={t('builder.fieldRackKind')}
          hint={isFloor ? t('builder.kindFloorHint') : undefined}
          value={values.kind}
          options={[
            { value: 'RACK', label: t('builder.kindRack') },
            { value: 'FLOOR', label: t('builder.kindFloor') },
          ]}
          onChange={(event) => setKind(event.target.value as RackKind)}
        />
      ) : null}

      <div className="grid grid-cols-2 gap-3">
        <Input
          mono
          label={isFloor ? t('builder.fieldFloorCode') : t('builder.fieldRackCode')}
          value={values.code}
          error={show(errors.code)}
          onChange={(event) => set('code', event.target.value)}
        />
        <Input
          label={isFloor ? t('builder.fieldFloorName') : t('builder.fieldRackName')}
          value={values.name}
          onChange={(event) => set('name', event.target.value)}
        />
      </div>

      <section className="flex flex-col gap-2">
        <h4 className="text-text-muted text-xs font-medium tracking-wide uppercase">
          {t('builder.sectionOverall')}
        </h4>
        {/* Juftlab joylashgan: chapda UMUMIY o'lcham, o'ngda uni nechaga
            bo'lish kerakligi. Ikkisi yonma-yon turgani muhim — operator
            "2 metr, bitta o'rin 1 metr" deb o'ylaydi va natijani (2 o'rin)
            pastdagi ro'yxatda darhol ko'radi. */}
        <div className="grid grid-cols-2 gap-3">
          <TotalInput
            label={t('builder.fieldTotalLength')}
            hint={t('builder.fieldTotalLengthHint')}
            valueCm={Math.round(sumOf(values.bays) / UNIT_M)}
            onCommit={(centimetres) => setLength(centimetres * UNIT_M)}
          />
          {/* MAYDONDA QAVAT YO'Q — balandlik bitta va u chegara:
              «bundan baland taxlanmaydi». Shuning uchun «bitta yarus»
              maydoni ham yo'q: bo'ladigan narsa yo'q. */}
          {isFloor ? (
            <Input
              mono
              type="number"
              min={1}
              step={10}
              label={t('builder.fieldStackHeight')}
              hint={t('builder.fieldStackHeightHint')}
              suffix={t('builder.unitCm')}
              error={show(errors.levelHeights)}
              value={cm(values.levelHeights[0] ?? 0)}
              onChange={(event) => set('levelHeights', [toMetres(event.target.value)])}
            />
          ) : (
            <>
              <TotalInput
                label={t('builder.fieldTotalHeight')}
                hint={t('builder.fieldTotalHeightHint')}
                valueCm={Math.round(rackHeightM(values) / UNIT_M)}
                onCommit={(centimetres) => resplitLevels(centimetres * UNIT_M, levelCm * UNIT_M)}
              />
              <TotalInput
                label={t('builder.fieldLevelHeight')}
                hint={t('builder.fieldLevelHeightHint')}
                valueCm={levelCm}
                onCommit={(centimetres) => {
                  setLevelCm(centimetres);
                  resplitLevels(rackHeightM(values), centimetres * UNIT_M);
                }}
              />
            </>
          )}
          <Input
            mono
            type="number"
            min={1}
            step={10}
            label={t('builder.fieldDepth')}
            suffix={t('builder.unitCm')}
            error={show(errors.depthM)}
            value={cm(values.depthM)}
            onChange={(event) => set('depthM', toMetres(event.target.value))}
          />
          {/* Burilish gradusda emas, IKKI TANLOV: javon rejada yo bo'yiga,
              yo eniga turadi va uchinchi holat ombor amaliyotida yo'q.
              "45°" kiritish mumkin bo'lgan maydon shunchaki xato manbai. */}
          <Select
            label={t('builder.fieldRotation')}
            value={String(values.rotationDeg)}
            options={[
              { value: '0', label: t('builder.orientAlong') },
              { value: '90', label: t('builder.orientAcross') },
            ]}
            onChange={(event) => set('rotationDeg', Number(event.target.value))}
          />
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <h4 className="text-text-muted text-xs font-medium tracking-wide uppercase">
          {t('builder.sectionInside')}
        </h4>
        <PartList
          mode="divide"
          hint={isFloor ? t('builder.partSpotsHint') : t('builder.partBaysHint')}
          label={isFloor ? t('builder.partSpots') : t('builder.partBays')}
          itemLabel={isFloor ? t('builder.partSpot') : t('builder.partBay')}
          unit={t('builder.unitCm')}
          values={values.bays}
          error={show(errors.bays)}
          onChange={(next) => set('bays', next)}
        />
        {isFloor ? null : (
          <PartList
            mode="append"
            label={t('builder.partLevels')}
            itemLabel={t('builder.partLevel')}
            unit={t('builder.unitCm')}
            values={values.levelHeights}
            error={show(errors.levelHeights)}
            onChange={(next) => set('levelHeights', next)}
          />
        )}
      </section>

      <p className="text-text-muted text-sm">{t('builder.cellTotal', { count: total })}</p>

      {added > 0 && rack !== null ? (
        <p className="text-text-muted text-sm">{t('builder.cellsAdded', { count: added })}</p>
      ) : null}

      {removed > 0 ? (
        <p role="alert" className="text-warn text-sm">
          {t('builder.cellsRemoved', { count: removed })}
        </p>
      ) : null}

      <div className="flex items-center gap-2">
        <Button type="submit" size="sm">
          {submitLabel ?? t('builder.save')}
        </Button>
        {onCancel === undefined ? null : (
          <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
            {t('builder.cancel')}
          </Button>
        )}
      </div>
    </form>
  );
};
